import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermission } from '@/components/guards/permission-guard';
import {
  ArrowLeft,
  FileText,
  User,
  Calendar,
  Package,
  Loader2,
  Pencil,
  CheckCircle,
  Clock,
  XCircle,
  Send,
  RefreshCw,
  Printer,
  Mail,
  Phone,
  ArrowRightCircle,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CounterpartyBadge } from '@/components/trading/sales/counterparty-badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { cn } from '@/lib/utils';

const statusConfig: Record<string, { color: string; icon: any }> = {
  DRAFT: { color: 'bg-gray-100 text-gray-700', icon: Clock },
  SENT: { color: 'bg-blue-100 text-blue-700', icon: Send },
  ACCEPTED: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  REJECTED: { color: 'bg-red-100 text-red-700', icon: XCircle },
  EXPIRED: { color: 'bg-amber-100 text-amber-700', icon: Clock },
  CONVERTED: { color: 'bg-purple-100 text-purple-700', icon: RefreshCw },
};

export function QuoteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const canEdit = usePermission('SALES_QUOTES', 'EDIT');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false);

  const { data: quote, isLoading, error } = trpc.tradingSalesQuote.byId.useQuery(
    id!,
    { enabled: !!id }
  );

  const utils = trpc.useUtils();

  const sendMutation = trpc.tradingSalesQuote.send.useMutation({
    onSuccess: () => {
      utils.tradingSalesQuote.byId.invalidate(id!);
      showSuccessToast(t('sales.quotes.sentSuccess'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const acceptMutation = trpc.tradingSalesQuote.accept.useMutation({
    onSuccess: () => {
      utils.tradingSalesQuote.byId.invalidate(id!);
      showSuccessToast(t('sales.quotes.acceptedSuccess'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const rejectMutation = trpc.tradingSalesQuote.reject.useMutation({
    onSuccess: () => {
      utils.tradingSalesQuote.byId.invalidate(id!);
      showSuccessToast(t('sales.quotes.rejectedSuccess'));
      setRejectConfirmOpen(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const convertMutation = trpc.tradingSalesQuote.convertToOrder.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('sales.quotes.convertedSuccess'));
      navigate(`/trading/sales/${data.id}`);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const deleteMutation = trpc.tradingSalesQuote.delete.useMutation({
    onSuccess: () => {
      showSuccessToast(tCommon('deleted'));
      navigate('/trading/sales/quotes');
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const formatCurrency = (value: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(value);
  };

  const formatDate = (dateValue: string | Date) => {
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return t('sales.quotes.draft');
      case 'SENT':
        return t('sales.quotes.sent');
      case 'ACCEPTED':
        return t('sales.quotes.accepted');
      case 'REJECTED':
        return t('sales.quotes.rejected');
      case 'EXPIRED':
        return t('sales.quotes.expired');
      case 'CONVERTED':
        return t('sales.quotes.converted');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">{tCommon('loading')}</span>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/trading/sales/quotes')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('sales.quotes.backToList')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{tCommon('error')}</p>
          <p className="text-sm">{error?.message || t('sales.quotes.notFound')}</p>
        </div>
      </div>
    );
  }

  const config = statusConfig[quote.status] ?? statusConfig.DRAFT;
  const StatusIcon = config!.icon;

  const isExpired = new Date(quote.validUntil) < new Date();
  const canSend = quote.status === 'DRAFT' && canEdit;
  const canAcceptReject = quote.status === 'SENT' && !isExpired;
  const canConvert = quote.status === 'ACCEPTED' && canEdit;
  const canDelete = quote.status === 'DRAFT' && canEdit;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/trading/sales/quotes')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('sales.quotes.backToList')}
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center bg-blue-100">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-mono">{quote.quoteNumber}</h1>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium',
                    config!.color
                  )}
                >
                  <StatusIcon className="h-3 w-3" />
                  {getStatusLabel(quote.status)}
                </span>
                {isExpired && quote.status === 'SENT' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
                    <Clock className="h-3 w-3" />
                    {t('sales.quotes.expired')}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground">
                {t('sales.quotes.createdOn')} {formatDate(quote.createdAt)}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && quote.status === 'DRAFT' && (
            <Button variant="outline" onClick={() => navigate(`/trading/sales/quotes/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              {tCommon('edit')}
            </Button>
          )}
          {canSend && (
            <Button onClick={() => sendMutation.mutate(id!)}>
              {sendMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              {t('sales.quotes.send')}
            </Button>
          )}
          {canAcceptReject && (
            <>
              <Button onClick={() => acceptMutation.mutate(id!)}>
                {acceptMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <ThumbsUp className="mr-2 h-4 w-4" />
                {t('sales.quotes.accept')}
              </Button>
              <Button variant="outline" onClick={() => setRejectConfirmOpen(true)}>
                <ThumbsDown className="mr-2 h-4 w-4" />
                {t('sales.quotes.reject')}
              </Button>
            </>
          )}
          {canConvert && (
            <Button onClick={() => convertMutation.mutate(id!)}>
              {convertMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <ArrowRightCircle className="mr-2 h-4 w-4" />
              {t('sales.quotes.convertToOrder')}
            </Button>
          )}
          {canDelete && (
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(true)}>
              <XCircle className="mr-2 h-4 w-4" />
              {tCommon('delete')}
            </Button>
          )}
          <Button variant="outline">
            <Printer className="mr-2 h-4 w-4" />
            {tCommon('print')}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('sales.quotes.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={() => deleteMutation.mutate(id!)}
        isLoading={deleteMutation.isPending}
      />

      {/* Reject Confirmation */}
      <ConfirmationDialog
        open={rejectConfirmOpen}
        onOpenChange={setRejectConfirmOpen}
        title={t('sales.quotes.reject')}
        description={t('sales.quotes.rejectConfirm')}
        confirmLabel={t('sales.quotes.reject')}
        variant="destructive"
        onConfirm={() => rejectMutation.mutate({ id: id! })}
        isLoading={rejectMutation.isPending}
      />

      {/* Converted Order Notice */}
      {quote.status === 'CONVERTED' && quote.salesOrders && quote.salesOrders.length > 0 && (
        <div className="border border-purple-200 bg-purple-50 p-4">
          <p className="text-sm text-purple-700">
            {t('sales.quotes.convertedTo')}:{' '}
            {quote.salesOrders.map((order, index) => (
              <span key={order.id}>
                {index > 0 && ', '}
                <Link
                  to={`/trading/sales/${order.id}`}
                  className="font-mono font-medium hover:underline"
                >
                  {order.orderNumber}
                </Link>
              </span>
            ))}
          </p>
        </div>
      )}

      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">{t('sales.quotes.tabs.details')}</TabsTrigger>
          <TabsTrigger value="items">{t('sales.quotes.tabs.items')}</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {/* Customer Info */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <User className="h-5 w-5" />
                {t('sales.quotes.customer')}
                <CounterpartyBadge kind={(quote as any).counterpartyKind} className="ml-2" />
              </h2>
              <div className="space-y-3 text-sm">
                {quote.companyName && (
                  <div>
                    <span className="text-muted-foreground">{t('sales.quotes.company')}</span>
                    <p className="font-medium">{quote.companyName}</p>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">{t('sales.quotes.contactName')}</span>
                  <p className="font-medium">{quote.contactName}</p>
                </div>
                {quote.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${quote.email}`} className="text-primary hover:underline">
                      {quote.email}
                    </a>
                  </div>
                )}
                {quote.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <a href={`tel:${quote.phone}`} className="hover:underline">
                      {quote.phone}
                    </a>
                  </div>
                )}
                {quote.tradingCustomer && (
                  <div className="pt-2 border-t">
                    <Link
                      to={`/trading/customers/${quote.tradingCustomer.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {t('sales.quotes.viewCustomer')}
                    </Link>
                  </div>
                )}
                {(quote as any).tradingSupplier && (
                  <div className="pt-2 border-t">
                    <Link
                      to={
                        (quote as any).tradingSupplier.supplierType === 'DEALER'
                          ? `/trading/cari/dealers/${(quote as any).tradingSupplier.id}`
                          : (quote as any).tradingSupplier.supplierType === 'SATICI'
                            ? `/trading/cari/sellers/${(quote as any).tradingSupplier.id}`
                            : `/trading/cari/suppliers/${(quote as any).tradingSupplier.id}`
                      }
                      className="text-sm text-primary hover:underline"
                    >
                      {t('cari.viewVendor')}
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Quote Details */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <Calendar className="h-5 w-5" />
                {t('sales.quotes.quoteDetails')}
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.quotes.quoteDate')}</span>
                  <span className="font-medium">{formatDate(quote.quoteDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.quotes.validUntil')}</span>
                  <span className={cn('font-medium', isExpired && 'text-red-600')}>
                    {formatDate(quote.validUntil)}
                    {isExpired && ` (${t('sales.quotes.expired')})`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.quotes.currency')}</span>
                  <span className="font-medium">{quote.currency}</span>
                </div>
                {quote.paymentTerms && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.quotes.paymentTerms')}</span>
                    <span className="font-medium">{quote.paymentTerms}</span>
                  </div>
                )}
                {quote.deliveryTerms && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('sales.quotes.deliveryTerms')}</span>
                    <span className="font-medium">{quote.deliveryTerms}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Created By */}
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <User className="h-5 w-5" />
                {t('sales.quotes.createdBy')}
              </h2>
              <div className="space-y-3 text-sm">
                {quote.createdBy && (
                  <>
                    <div>
                      <span className="text-muted-foreground">{tCommon('name')}</span>
                      <p className="font-medium">
                        {quote.createdBy.firstName} {quote.createdBy.lastName}
                      </p>
                    </div>
                    {quote.createdBy.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span>{quote.createdBy.email}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('sales.quotes.createdOn')}</span>
                  <span className="font-medium">{formatDate(quote.createdAt)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Summary */}
          <div className="border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 font-semibold mb-4">
              <Package className="h-5 w-5" />
              {t('sales.quotes.summary')}
            </h2>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{quote.items?.length || 0}</p>
                <p className="text-sm text-muted-foreground">{t('sales.quotes.items')}</p>
              </div>
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(quote.subtotal, quote.currency)}</p>
                <p className="text-sm text-muted-foreground">{t('sales.quotes.subtotal')}</p>
              </div>
              <div className="text-center p-4 bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(quote.taxAmount, quote.currency)}</p>
                <p className="text-sm text-muted-foreground">{t('sales.quotes.tax')}</p>
              </div>
              <div className="text-center p-4 bg-blue-100">
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(quote.total, quote.currency)}</p>
                <p className="text-sm text-blue-600">{t('sales.quotes.total')}</p>
              </div>
            </div>
          </div>

          {/* Discount Info */}
          {quote.discountPercent > 0 && (
            <div className="border border-border bg-card p-6">
              <h2 className="font-semibold mb-2">{t('sales.quotes.discount')}</h2>
              <p className="text-sm">
                {t('sales.quotes.globalDiscount')}: <span className="font-medium">{quote.discountPercent}%</span>
                {' '}({formatCurrency(quote.discountAmount, quote.currency)})
              </p>
            </div>
          )}

          {/* Notes */}
          {quote.notes && (
            <div className="border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-semibold mb-4">
                <FileText className="h-5 w-5" />
                {t('sales.quotes.notes')}
              </h2>
              <p className="text-sm whitespace-pre-wrap">{quote.notes}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <div className="border border-border bg-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('sales.quotes.product')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.quantity')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.unitPrice')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.itemDiscount')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.tax')}</th>
                  <th className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.lineTotal')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {quote.items?.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-sm">{item.productName}</p>
                        <p className="text-xs text-muted-foreground font-mono">{item.productCode}</p>
                        {item.notes && (
                          <p className="text-xs text-muted-foreground mt-1">{item.notes}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatCurrency(item.unitPrice, quote.currency)}</td>
                    <td className="px-4 py-3 text-right text-sm">{item.discountPercent}%</td>
                    <td className="px-4 py-3 text-right text-sm">{item.taxRate}%</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(item.lineTotal, quote.currency)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan={4} />
                  <td className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.subtotal')}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(quote.subtotal, quote.currency)}</td>
                </tr>
                {quote.discountAmount > 0 && (
                  <tr>
                    <td colSpan={4} />
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                      {t('sales.quotes.discount')} ({quote.discountPercent}%)
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                      -{formatCurrency(quote.discountAmount, quote.currency)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={4} />
                  <td className="px-4 py-3 text-right text-sm font-medium">{t('sales.quotes.tax')}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(quote.taxAmount, quote.currency)}</td>
                </tr>
                <tr className="bg-blue-50">
                  <td colSpan={4} />
                  <td className="px-4 py-3 text-right text-sm font-bold">{t('sales.quotes.total')}</td>
                  <td className="px-4 py-3 text-right text-sm font-bold">{formatCurrency(quote.total, quote.currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
